import { type AggResponse, fetchAgg } from '../../../common/utils/fetch-agg';
import { inMemoryCache } from '../../../utils/caching/in-memory';

export const positionStatusesCache: {
	get: (key: string) => Promise<AggResponse<PositionStatusesResponse>> | undefined;
	set: (key: string, result: Promise<AggResponse<PositionStatusesResponse>>) => void;
	has: (key: string) => boolean;
	clear: (key?: string) => void;
	inMemoryDecorator: (
		key: string,
		supplier: () => Promise<AggResponse<PositionStatusesResponse>>,
		expireWhen?: ((result: AggResponse<PositionStatusesResponse>) => boolean) | undefined,
	) => Promise<AggResponse<PositionStatusesResponse>>;
} = inMemoryCache<AggResponse<PositionStatusesResponse>>();

const QUERY_NAME = 'PositionStatuses';
const positionStatusesQueryString = `query ${QUERY_NAME}($cloudId: ID!, $fieldId: ID!, $rql: String, $after: String, $first: Int) {
  radar_fieldValues(
    cloudId: $cloudId
    uniqueFieldId: $fieldId
    rql: $rql
    first: $first
    after: $after
  ) { 
    totalCount
    pageInfo {
      endCursor
      hasNextPage
    }
    edges {
      cursor
      node {
        ... on RadarStatusFieldValue {
          __typename
          isRestricted
          statusValue: value
          statusDisplayValue: displayValue
          appearance
        }
      }
    }
  }
}`;

export type PositionStatus = {
	cursor: string;
	node: {
		isRestricted: boolean;
		statusValue: string;
		statusDisplayValue: string;
		appearance: string;
	};
};

export type PositionStatusesResponse = {
	radar_fieldValues: {
		edges?: PositionStatus[];
	};
};

export type PositionStatusesQueryVariables = {
	cloudId: string;
	first: number;
	fieldId: string;
	rql: string;
	after?: string;
};

export const positionStatusesQuery: (
	cloudId: string,
	first: number | undefined,
	rql: string,
	after?: string,
	headers?: Record<string, string>,
) => Promise<AggResponse<PositionStatusesResponse>> = (
	cloudId: string,
	first = 20,
	rql: string,
	after?: string,
	headers?: Record<string, string>,
) => {
	const fieldId = 'positionStatus';
	return positionStatusesCache.inMemoryDecorator(
		[cloudId, first, rql, fieldId, after].filter(Boolean).join('-'),
		async () => {
			if (!cloudId) {
				return {
					data: {
						radar_fieldValues: {
							edges: [],
						},
					},
				};
			}

			const response = await fetchAgg<PositionStatusesQueryVariables, PositionStatusesResponse>({
				graphQuery: positionStatusesQueryString,
				variables: { cloudId, first, rql, fieldId, after },
				operationName: QUERY_NAME,
				headers,
			});

			const hasErrors = response.errors?.some((error) => error);

			if (hasErrors) {
				return Promise.reject(response);
			}
			return response;
		},
	);
};
